import { VIDEO_PLATFORM } from '../modality/Modality'
import { MEETING_FORMATS } from '../session/Session'

export type TimeStamp = string

export type ProviderAvailability = {
  modality_type?: string
  location?: object
  start: TimeStamp
  end: TimeStamp
}

// NOT_FOUND = 'Non connected calendar providers'
export enum PROVIDER_AVAILABILITY_ERROR_DETAIL {
  UNKNOWN = 'UNKNOWN',
  NOT_FOUND = 'NOT_FOUND',
  NO_WRITEABLE_CALENDAR = 'NO_WRITEABLE_CALENDAR',
  INVALID_CONFIGURATION = 'INVALID_CONFIGURATION',
}

export enum PROVIDER_CALENDAR_USAGE {
  READ_BUSY = 'READ_BUSY',
  WRITE_APPOINTMENTS = 'WRITE_APPOINTMENTS',
  WRITE_AVAILABILITY = 'WRITE_AVAILABILITY',
  WRITE_ALL = 'WRITE_ALL',
}

// The possible admin statuses for a given calendar configuration
export enum CALENDAR_CONFIGURATION_ADMIN_STATUS {
  HIDDEN = 'HIDDEN',
  PUBLISHED = 'PUBLISHED',
}

// A non-exhaustive list of access roles; three commonly seen roles
export enum CALENDAR_CONFIGURATION_ACCESS_ROLE {
  OWNER = 'owner',
  READER = 'reader',
  WRITER = 'writer',
}

// We do not necessarily have an exhaustive list of the unacceptable access roles so we must use this list of valid ones
export const VALID_CALENDAR_ACCESS_ROLES: string[] = [
  CALENDAR_CONFIGURATION_ACCESS_ROLE.OWNER,
  CALENDAR_CONFIGURATION_ACCESS_ROLE.WRITER,
]

// The possible admin statuses for a provider record in CS
export enum CALENDAR_PROVIDER_ADMIN_STATUS {
  ACTIVE = 'ACTIVE',
  DELETE_PENDING = 'DELETE_PENDING',
  SYNC_PENDING = 'SYNC_PENDING',
}

export type ProviderAvailabilities = {
  provider_id: string
  status: 'VALID' | 'ERROR'
  error_detail?: PROVIDER_AVAILABILITY_ERROR_DETAIL
  lead_time_hours: number
  appointment_duration_minutes: number
  availability: Array<ProviderAvailability>
  local_time_zone: string | null
  booking_mode?: BookingMode
}

export type AvailabilitiesResponse = {
  provider_ids: Array<string>
  provider_availability: Array<ProviderAvailabilities>
}
export type InPersonLocation = {
  city?: string
  country?: string
  id?: number
  name?: string
  state?: string
  street1?: string
  street2?: string
  zipcode?: string
  zip?: string
  location?: {
    lat?: number
    lon?: number
  }
}

export type AvailabilityDate = {
  date: string
  times: Array<{
    datetime: string | null
    modality_type?: ModalityType
    location?: InPersonLocation
  }>
}

export type ParsedAvailability = {
  appointmentDurationMinutes: number
  firstAvailability: TimeStamp
  lastAvailability: TimeStamp
  dates: Array<AvailabilityDate>
}

export type ProviderCalendarConfiguration = {
  access_role: string
  calendar_id: string
  calendar_system: string
  calendar_name: string
  calendar_usage: PROVIDER_CALENDAR_USAGE
  owner_account_id?: string
  id: string
  status: 'VALID' | 'ERROR'
  read_only?: boolean
  time_zone: string
  is_primary: boolean
  admin_status: string
}

export type ProviderCalendarEvent = {
  event_type: string
  event_origin: string
  id: string
  provider_calendar_configuration_id: string
  external_id: string
  creator_email: string
  location: string | null
  title: string
  description: string
  start_datetimetz: string
  end_datetimetz: string
  time_zone: string
  recurring_event_id: string | null
  is_blocked: boolean
  has_conference_data: boolean
  booking_type?: string[] | null
  extended_properties?: {
    private?: Dict
    shared?: Dict
  }
}

export type LyraEventExtendedProps = {
  lyraEventType: 'session'
  lyraProgramId: string | null | undefined
  appointmentClass: 'initial' | 'recurring'
  lyraAppointmentId: number
  clientFirstName: string | undefined
  clientLastName: string | undefined
  clientPhoneNumber: string | undefined
  clientEmail: string | undefined
  appointmentSessionNumber: number
  appointmentMeetingFormat: MEETING_FORMATS
  appointmentAddress?: string
  appointmentVideoPlatform?: VIDEO_PLATFORM
  clientTimeZone: string | null
  clientId: string | null
  isNoShow: boolean
  appointmentBookedDetails?: AppointmentBookedDetails
  appointmentStatus?: string
  appointmentType?: string
  treatmentType?: string
  meetingFormat?: MEETING_FORMATS
  modality_availability?: EventModality | null
  isChargeReversed?: boolean
  isCoveredLateCancel?: boolean
  isClientOwedLateCancel?: boolean
  guardianLyraId?: string
  childFirstName?: string
  guardianEmail?: string
  guardianFirstName?: string
  guardianLastName?: string
  guardianPhone?: string
  providerScheduled?: boolean
}

export const BEND_APPOINTMENT_TYPES = [
  'bcm_evaluation',
  'bcm_session',
  'coach_evaluation',
  'coach_session',
  'therapist_evaluation',
  'therapist_session',
  'prescriber_evaluation',
  'prescriber_session',
  'nurse_session',
  'psychometrist_evaluation',
  'psychometrist_session',
  'neuropsychologist_session',
] as const

export type BendAppointmentType = (typeof BEND_APPOINTMENT_TYPES)[number]

export const isBendAppointmentType = (value: unknown): value is BendAppointmentType =>
  (BEND_APPOINTMENT_TYPES as readonly unknown[]).includes(value)

export type BendEventExtendedProps = {
  lyraEventType: 'session'
  bendAppointmentType: BendAppointmentType
  externalId: string
  clientTimeZone: string | null
  description: string | null
  calendarConfigurationId: string
  calendarName: string
  isPrivateEvent: boolean
  bendAppointmentId: string
}

export const isBendEvent = (props: EventExtendedPropsBase): props is BendEventExtendedProps =>
  'bendAppointmentType' in props && 'bendAppointmentId' in props

export type LyraEventExtendedPropsNoTZ = Omit<LyraEventExtendedProps, 'clientTimeZone'>

export type BookableEventExtendedProps = {
  lyraEventType: 'bookable' | 'bookable_recurring'
  externalId: string
  booking_type?: string[] | null
}

export type GenericEventExtendedProps = {
  lyraEventType: 'generic'
  description: string | null
  externalId: string
  calendarConfigurationId: string
  hasConferenceData: boolean
  clientTimeZone: string | null
  lyraAppointmentId: number | null
  isOOOEvent: boolean
  isPrivateEvent: boolean
  calendarName: string
}

export type EventExtendedPropsBase =
  | LyraEventExtendedProps
  | BookableEventExtendedProps
  | GenericEventExtendedProps
  | BendEventExtendedProps

export enum NpSessionType {
  NP_CLINICAL_INTAKE = 'NP_CLINICAL_INTAKE',
  NP_TESTING_SESSION = 'NP_TESTING_SESSION',
}

export type BuiltEvent = {
  id?: string
  startTime: string
  endTime: string
  modality?: EventModality | null
  title?: string | null
  booking_type?: string[] | null
}

export type EventExtendedProps = EventExtendedPropsBase & {
  hovered?: boolean
  hasConflict?: boolean
  treatmentType?: string
  outsideBookingHours?: boolean
  meetingFormat?: MEETING_FORMATS
  modality_availability?: EventModality | null
  booking_type?: string[] | null
}

type ConferenceDataEntryPoint = {
  entryPointType: string
  label: string
  meetingCode: string
  passcode: string
  uri: string
}
export type ProviderGoogleEvent = {
  kind: string
  etag: string
  id: string
  status: string
  htmlLink: string
  created: string
  updated: string
  summary: string
  description: string
  creator: {
    email: string
  }
  organizer: {
    email: string
    displayName: string
    self: boolean
  }
  start: {
    dateTime: string
    timeZone: string
  }
  end: {
    dateTime: string
    timeZone: string
  }
  iCalUID: string
  sequence: number
  attendees: {
    email: string
    displayName: string
    organizer: boolean
    self: boolean
    responseStatus: string
  }[]
  reminders: {
    useDefault: boolean
  }
  eventType: string
  conferenceData?: {
    entryPoints: ConferenceDataEntryPoint[]
  }
}

export enum BookingMode {
  FREEBUSY = 'freebusy',
  BOOKABLE = 'bookable',
  HYBRID = 'hybrid',
  UNSET = 'unset',
  // BEND provider booking modes
  HYBRID_BLOCK = 'hybrid_block',
  BOOKABLE_BLOCK = 'bookable_block',
  HYBRID_MANAGED = 'hybrid_managed',
}

/**
 * Strategy for showing conflict markers on calendar events.
 * - DEFAULT: Original behavior - both sessions and availability get conflict markers
 * - IGNORE_AVAILABILITY: Only sessions are checked for conflicts (availability excluded)
 * - EVENT_MARKER_ONLY: Both are checked, but only the "other event" gets the marker (not availability)
 */
export enum ConflictStrategy {
  DEFAULT = 'default',
  IGNORE_AVAILABILITY = 'ignoreAvailability',
  EVENT_MARKER_ONLY = 'eventMarkerOnly',
}

//These indicate whether PP-API should query for appointments by active episodes or for a date range
export enum CalendarAppointmentsView {
  EPISODIC = 'episodic',
  CALENDAR = 'calendar',
}

export type CalendarProvider = {
  admin_status?: CALENDAR_PROVIDER_ADMIN_STATUS
  booking_mode: BookingMode
  daily_end?: string
  daily_start?: string
  timezone?: string
}

export enum CalendarAlertType {
  SCHEDULED,
  BOOKABLE,
}

type CalendarAlertAction = {
  label: React.ReactNode
  onClick: () => void
  isLoading?: boolean
  testID?: string
}

export type CalendarAlert = {
  ariaLabel: string
  type: CalendarAlertType
  badge: React.ReactNode
  title: React.ReactNode
  body: React.ReactNode
  actions?: {
    primary: CalendarAlertAction
    secondary?: CalendarAlertAction
  }
  trackingEvent: string
  closeTrackingAction?: string
}

export type CalendarAlertArgs = {
  timeOffHours: number
  clientHours: number
  startDate: string
  scheduledSessions: number
  overlappingSlots: string[]
}

export type AppointmentBookedDetails = {
  appointmentForChild: boolean
  child_firstName: string
  child_lastName: string
  child_clientDob: string
  child_isMinor: boolean
  childFirstName?: string
  childPhone?: string
  guardianLyraId?: string
  guardianEmail?: string
  guardianFirstName?: string
  guardianLastName?: string
  guardianPhone?: string
  clientName: string
  clientFirstName?: string
  clientLastName?: string
  clientDob: string
  clientContactInfo: string
  clientPhone?: string
  clientPronouns?: string[]
  apptStartTime: string
  typeOfPatient: string
  treatmentType: string
  primaryNeed: string
  modality: string
  customer: string
  highAlertObject: HighAlertObject
  // The following are not returned by the appointment-booked-details endpoint. They are populated
  // client-side (from the client record) for Military OneSource clients so the calendar popover can
  // display MOS-relevant info. See ui-providerpayments CurrentCalendar.getAppointmentBookedDetails.
  isMos?: boolean
  mosId?: string
  authorizationNumber?: string
  lyraCode?: string
}

type HighAlertObject = {
  keyRiskSI: string
  siInPast2Weeks: string
  siFrequency: string
  siPlan: string
  siPlanInTheNext24Hours: string
  siStepsTaken: string
  hospitalized: string
  riskLevel: 'yellow' | 'amber' | 'red'
}

export type EventModality = {
  video: { available: boolean; platform?: VIDEO_PLATFORM }
  in_person: {
    available: boolean
    location?: InPersonLocation
  }
}

export const isSameModality = (newEvent?: EventModality | null, oldEvent?: EventModality | null) => {
  return (
    newEvent?.video.available === oldEvent?.video.available &&
    newEvent?.video.platform === oldEvent?.video.platform &&
    newEvent?.in_person.available === oldEvent?.in_person.available &&
    newEvent?.in_person.location?.id === oldEvent?.in_person.location?.id
  )
}

export const isSameBookingType = (newBookingType?: string[] | null, oldBookingType?: string[] | null) => {
  if (!newBookingType && !oldBookingType) return true
  if (!newBookingType || !oldBookingType) return false
  if (newBookingType.length !== oldBookingType.length) return false
  return newBookingType.every((type, index) => type === oldBookingType[index])
}

export function getNpSessionLabel(type: NpSessionType, defaultTitle?: string) {
  switch (type) {
    case NpSessionType.NP_CLINICAL_INTAKE:
      return 'Intake'
    case NpSessionType.NP_TESTING_SESSION:
      return 'Testing'
    default:
      return defaultTitle
  }
}

export function getNpSessionTypeFromBookingType(bookingType?: string[] | null): NpSessionType {
  if (bookingType?.includes(NpSessionType.NP_TESTING_SESSION)) {
    return NpSessionType.NP_TESTING_SESSION
  }
  return NpSessionType.NP_CLINICAL_INTAKE
}

export enum ModalityType {
  BOTH_IN_PERSON_AND_VIDEO = 'both',
  IN_PERSON = 'in_person',
  VIDEO = 'video',
}
